import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useNegocio } from './AliadoDashboardLayout';
import { ShoppingCart, Package, TrendingUp, Users, GitBranch, ChevronRight, AlertCircle, CheckCircle2, Clock, XCircle } from 'lucide-react';

const STATUS_CONFIG = {
    new: { label: 'Nuevo', color: 'bg-blue-100 text-blue-700' },
    preparing: { label: 'Preparando', color: 'bg-amber-100 text-amber-700' },
    ready: { label: 'Listo', color: 'bg-emerald-100 text-emerald-700' },
    delivered: { label: 'Entregado', color: 'bg-neutral-100 text-neutral-600' },
    cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-600' },
};

function StatCard({ icon: Icon, label, value, to, color = 'text-orange-600' }) {
    const content = (
        <div className="rounded-2xl border bg-card p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Icon className={`h-3.5 w-3.5 ${color}`} />
                {label}
            </div>
            <div className="text-2xl font-bold tabular-nums">{value}</div>
        </div>
    );
    if (to) return <Link to={to}>{content}</Link>;
    return content;
}

export default function AliadoDashboardHome() {
    const negocio = useNegocio();
    const [stats, setStats] = useState(null);
    const [recentOrders, setRecentOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!negocio) return;
        Promise.all([
            api.get('/me/oknegocio/sales'),
            api.get('/me/oknegocio/orders?limit=5'),
            api.get('/me/oknegocio/products'),
            api.get('/me/oknegocio/squad'),
            api.get('/me/oknegocio/branches'),
        ]).then(([sales, orders, products, squad, branches]) => {
            setStats({
                revenue: sales.data.total_revenue,
                totalOrders: sales.data.total_orders,
                products: products.data.length,
                squad: squad.data.length,
                branches: branches.data.length,
                activeOrders: (orders.data.items || []).filter(o => ['new', 'preparing', 'ready'].includes(o.status)).length,
            });
            setRecentOrders(orders.data.items || []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [negocio]);

    if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando resumen…</div>;

    const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-bold">¡Hola, {negocio?.name}!</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Aquí tienes un resumen de tu negocio.</p>
            </div>

            {negocio?.status === 'pending' && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium text-amber-800 text-sm">Tu negocio está en revisión</div>
                        <div className="text-xs text-amber-700 mt-0.5">Una vez aprobado, los clientes podrán encontrarte en OkNegocios.</div>
                    </div>
                </div>
            )}

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <StatCard icon={TrendingUp} label="Ingresos totales" value={fmt(stats?.revenue)} color="text-emerald-600" />
                <StatCard icon={ShoppingCart} label="Órdenes totales" value={stats?.totalOrders ?? '—'} to="/aliado/dashboard/orders" />
                <StatCard icon={Package} label="Productos" value={stats?.products ?? '—'} to="/aliado/dashboard/products" color="text-blue-600" />
                <StatCard icon={Users} label="Equipo" value={stats?.squad ?? '—'} to="/aliado/dashboard/squad" color="text-violet-600" />
                <StatCard icon={GitBranch} label="Sucursales" value={stats?.branches ?? '—'} to="/aliado/dashboard/branches" color="text-teal-600" />
                <StatCard icon={ShoppingCart} label="Órdenes activas" value={stats?.activeOrders ?? '—'} to="/aliado/dashboard/orders" color="text-orange-600" />
            </div>

            {/* Recent orders */}
            <div className="rounded-2xl border bg-card p-4">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-sm">Órdenes recientes</h2>
                    <Link to="/aliado/dashboard/orders" className="text-xs text-orange-600 hover:underline flex items-center gap-1">
                        Ver todas <ChevronRight className="h-3 w-3" />
                    </Link>
                </div>

                {recentOrders.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-6">
                        Aún no tienes órdenes. Comparte tu perfil para recibir pedidos.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {recentOrders.map(o => {
                            const cfg = STATUS_CONFIG[o.status] || { label: o.status, color: 'bg-neutral-100 text-neutral-600' };
                            return (
                                <div key={o.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                                    <div>
                                        <div className="text-sm font-medium">{o.customer_name}</div>
                                        <div className="text-xs text-muted-foreground">{o.items?.length ?? 0} artículo(s) · {o.payment_method}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-semibold">{fmt(o.total)}</span>
                                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Quick links */}
            <div className="grid grid-cols-2 gap-3">
                {[
                    { to: '/aliado/dashboard/products', label: 'Agregar producto', icon: Package },
                    { to: '/aliado/dashboard/orders', label: 'Gestionar órdenes', icon: ShoppingCart },
                    { to: '/aliado/dashboard/squad', label: 'Agregar colaborador', icon: Users },
                    { to: '/aliado/dashboard/sales', label: 'Ver reporte de ventas', icon: TrendingUp },
                ].map(({ to, label, icon: Icon }) => (
                    <Link key={to} to={to}
                        className="rounded-2xl border bg-card p-3 flex items-center gap-3 hover:bg-orange-50 transition-colors">
                        <Icon className="h-4 w-4 text-orange-600" />
                        <span className="text-sm">{label}</span>
                    </Link>
                ))}
            </div>
        </div>
    );
}
